import React, { useRef, useState, useMemo, useEffect } from 'react';
import 'mapbox-gl/dist/mapbox-gl.css';
import mapboxgl from 'mapbox-gl';
import Map, {
  NavigationControl,
  Source,
  Layer,
  Marker,
  Popup,
} from 'react-map-gl';
import bbox from '@turf/bbox';
import PlaceIcon from '@mui/icons-material/Place';
import ReactGA from 'react-ga4';

import { useSelector, useDispatch } from 'react-redux';
import {
  updateViewState,
  changeShowCountyOverride,
  changeLocation,
  changeStation,
} from '../../store/map/map.slice';
import {
  changeEventDisplayVisible,
  changeOverviewTab,
} from '../../store/ui/ui.slice';
import { getProjectionDataAsync, getStationDataAsync } from '../../store/data/data.slice';
import {
  selectViewState,
  selectIsCountyLevel,
  selectShowCountyOverride,
  selectShowStations,
  mapboxToken
} from '../../store/map/map.selector';
import {
  selectMapColors,
  selectSearchLocation,
} from '../../store/data/data.selector';
import { getStateAbbr, validStateFips } from '../../store/constants/locationInfo';

import {
  selectIsClimate,
  selectTheme,
  selectAttributeInfo,
  selectAttributeId
} from '../../store/options/options.selector';
import { getHurricaneDataAsync } from '../../store/alternate-data/alternate-data.slice';
import {
  getStatesLayer,
  getCountiesLayer,
  getCountyLines,
  highlightStateLayer,
  highlightCountyLayer,
} from './layerStyles';

import MapPopup from './map-popup/map-popup.component';
import StationMarkers from './station-markers/station-markers.component';

import './map.styles.scss';

mapboxgl.workerClass =
  // eslint-disable-next-line
  require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;

// Set token for mapbox API
mapboxgl.accessToken = mapboxToken;

export default function MapComponent() {
  const [clickFilter, setClickFilter] = useState(['in', 'AFFGEOID', '']);
  const [popupData, setPopupData] = useState(null);
  const [markerPopup, setMarkerPopup] = useState(null);
  const [timeAt, setTimeAt] = useState(null);

  const dispatch = useDispatch();
  const viewState = useSelector(selectViewState);
  const mapColors = useSelector(selectMapColors);
  const isCountyLevel = useSelector(selectIsCountyLevel);
  const searchLocation = useSelector(selectSearchLocation);
  const showCountyOverride = useSelector(selectShowCountyOverride);
  const isClimate = useSelector(selectIsClimate);
  const theme = useSelector(selectTheme);
  const showStations = useSelector(selectShowStations);
  const attrInfo = useSelector(selectAttributeInfo);
  const attr = useSelector(selectAttributeId);
  const mapRef = useRef(null);

  useEffect(() => {
    if (searchLocation.lat && searchLocation.lng) {
      mapRef.current.flyTo({
        center: [searchLocation.lng, searchLocation.lat],
        zoom: 7
      });
    }
  }, [searchLocation]);

  const handleMarkerOpen = (lat, lng, name) => {
    setMarkerPopup({lat, lng, name});
  };

  const handleMarkerClose = () => {
    setMarkerPopup(null);
  };

  const handleMarkerClick = (event, loc) => {
    event.stopPropagation();
    mapRef.current.flyTo({
      center: [loc.lng, loc.lat],
      padding: {top: 0, bottom:0, left: 75, right: 0}
    });

    const params = {
      sid: loc.sid,
      sdate: String(attrInfo.params.sdate),
      edate: String(attrInfo.params.edate),
      elems: attrInfo.params.elems,
    };
    params.elems.forEach(elem => delete elem.area_reduce);
    dispatch(getStationDataAsync({params, loc: `${loc.lng},${loc.lat}`, attr}));
    dispatch(changeStation(loc));
  };

  // Increases zoom step and sets init zoom for reset button to use
  const handleLoad = (e) => {
    e.target.scrollZoom.setWheelZoomRate(1 / 800);
  };

  const handleClick = async (event) => {
    let feature = event.features[0];
    if (
      mapRef &&
      mapRef.current &&
      feature &&
      (feature.layer.id === 'states' || feature.layer.id === 'counties')
    ) {
      const [minLng, minLat, maxLng, maxLat] = bbox(feature);
      mapRef.current.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        {
          padding: { top: 100, bottom: 10, left: 75 },
        }
      );

      const newId =
        feature.layer.id === 'states'
          ? feature.properties.STUSPS
          : feature.properties.GEOID;

      setClickFilter(['in', 'AFFGEOID', feature.properties.AFFGEOID]);

      const newTime = new Date().getTime();
      if (timeAt !== null) {
        ReactGA.event({
          category: "User Interaction",
          action: "Time at Location (sec)",
          label: Math.round((newTime - timeAt) / 1000)
        });
      }
      setTimeAt(newTime);

      ReactGA.event({
        category: "User Interaction",
        action: `${feature.layer.id === 'states' ? 'State' : 'County'} Selected`,
        label: feature.properties.NAME
      });

      await Promise.all([
        dispatch(changeEventDisplayVisible(false)),
        dispatch(changeShowCountyOverride(true)),
        dispatch(changeOverviewTab('local')),
        dispatch(
          changeLocation({
            id: newId,
            name: feature.properties.NAME,
            state:
              feature.layer.id === 'counties'
                ? getStateAbbr(feature.properties.STATEFP)
                : null,
            geometry: feature.geometry,
          })
        ),
      ]);

      if (theme === 5) {
        dispatch(getHurricaneDataAsync(feature.geometry));
      }

      const arg = { type: 'changeLocation', payload: newId };
      if (isClimate) {
        dispatch(getProjectionDataAsync(arg));
      }
    }
  };

  const handlePan = (e) => {
    const newVS = e.viewState;

    // Clamp latitude to range
    if (newVS.latitude > 49.82 || newVS.latitude < 31.65) {
      newVS.latitude = viewState.latitude;
    }

    // Clamp longitude to range
    if (newVS.longitude > -71.13 || newVS.longitude < -98.85) {
      newVS.longitude = viewState.longitude;
    }

    dispatch(updateViewState(newVS));
  };

  const handleHover = (event) => {
    const feature = event.features && event.features[0];
    let popupInfo = null;
    if (feature) {
      const isState = feature.layer.id === 'states';
      popupInfo = {
        isState,
        id: isState ? feature.properties.STUSPS : feature.properties.GEOID,
        name: feature.properties.NAME,
        state: Object.keys(feature.properties).includes('STATEFP')
          ? feature.properties.STATEFP
          : '',
        latitude: event.lngLat.lat,
        longitude: event.lngLat.lng,
      };
    }

    setPopupData(popupInfo);
  };

  const handleEndHover = () => {
    setPopupData(null);
  };

  const handleZoom = (e) => {
    if (showCountyOverride && e.viewState.zoom < viewState.zoom) {
      dispatch(changeShowCountyOverride(false));
    }
  };

  const memoizedStatesLayer = useMemo(() => {
    return getStatesLayer(mapColors.stateColors, isCountyLevel, validStateFips);
  }, [mapColors, isCountyLevel]);

  const memoizedCountiesLayer = useMemo(() => {
    return getCountiesLayer(mapColors.countyColors, isCountyLevel, validStateFips);
  }, [mapColors, isCountyLevel]);

  const memoizedCountyLines = useMemo(() => {
    return getCountyLines(isCountyLevel, validStateFips);
  }, [isCountyLevel]);

  return (
    <div className='map-container'>
      <Map
        ref={mapRef}
        {...viewState}
        mapStyle='mapbox://styles/precipadmin/cmdzyuvyn00he01s9cj1f012h'
        onLoad={handleLoad}
        onClick={handleClick}
        onMouseMove={handleHover}
        onMouseLeave={handleEndHover}
        onMove={handlePan}
        onZoomStart={handleZoom}
        dragRotate={false}
        touchPitch={false}
        doubleClickZoom={false}
        attributionControl={false}
        interactiveLayerIds={['states', 'counties']}
      >
        <Source type='vector' url='mapbox://precipadmin.5jkz4zwy'>
          <Layer beforeId='all-state-outlines' {...memoizedStatesLayer} />
        </Source>
        <Source type='vector' url='mapbox://precipadmin.b2ztd99d'>
          <Layer beforeId='all-state-outlines' {...memoizedCountiesLayer} />
        </Source>
        <Source type='vector' url='mapbox://precipadmin.b2ztd99d'>
          <Layer beforeId='all-state-outlines' {...memoizedCountyLines} />
        </Source>
        <Source type='vector' url='mapbox://precipadmin.5jkz4zwy'>
          <Layer
            beforeId='waterway-label'
            {...highlightStateLayer}
            filter={clickFilter}
          />
        </Source>
        <Source type='vector' url='mapbox://precipadmin.b2ztd99d'>
          <Layer
            beforeId='waterway-label'
            {...highlightCountyLayer}
            filter={clickFilter}
          />
        </Source>

        <NavigationControl position='bottom-right' showCompass={false} />

        {popupData !== null && !markerPopup && (
          <MapPopup popupData={popupData} />
        )}
        {markerPopup && (
          <Popup
            tipSize={5}
            longitude={markerPopup.lng}
            latitude={markerPopup.lat}
            offset={{
              right: [-20, -25],
              bottom: [0, -28],
            }}
            closeOnClick={false}
            closeButton={false}
            style={{ zIndex: 1 }}
          >
            {markerPopup.name}
          </Popup>
        )}
        {showStations && 
          <StationMarkers
            openMarker={handleMarkerOpen}
            closeMarker={handleMarkerClose}
            clickMarker={handleMarkerClick}
          />
        }
        {searchLocation.lat && searchLocation.lng && (
          <Marker latitude={searchLocation.lat} longitude={searchLocation.lng} anchor='bottom' style={{ zIndex: 1 }}>
            <div
              className='marker'
              onMouseEnter={() => handleMarkerOpen(searchLocation.lat, searchLocation.lng, searchLocation.name)}
              onMouseLeave={handleMarkerClose}
            >
              <PlaceIcon sx={{ color: 'red !important' }}/>
            </div>
          </Marker>
        )}
      </Map>
    </div>
  );
}
